import { formatXmlUnitPrice } from './currencyFormat';
import { calculatesZeroTaxAmount, effectiveTaxRateForCalculation } from './taxCategoryUtils';

// BigDecimal HALF_UP at scale 2, using the decimal text of the number.
export const halfUpToTwoDecimals = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }

  const negative = number < 0;
  let text = Math.abs(number).toString();
  if (/e/i.test(text)) {
    text = Math.abs(number)
      .toFixed(16)
      .replace(/\.?0+$/, '');
  }

  const [ints, fraction = ''] = text.split('.');
  const padded = `${fraction}000`.slice(0, 3);
  let cents = Number(ints) * 100 + Number(padded.slice(0, 2));
  if (Number(padded[2]) >= 5) {
    cents += 1;
  }

  const rounded = cents / 100;
  return negative ? -rounded : rounded;
};

// Amounts as integer units of 10^-10: the XML prints at most 10 fraction digits, and the API stores that scale.
const XML_UNIT = 10n ** 10n;
const CENT_UNITS = XML_UNIT / 100n;

const toXmlUnits = (value) => {
  const text = formatXmlUnitPrice(Number(value) || 0);
  const negative = text.startsWith('-');
  const [integer, fraction = ''] = (negative ? text.slice(1) : text).split('.');
  const units = BigInt(integer) * XML_UNIT + BigInt(fraction.padEnd(10, '0'));

  return negative ? -units : units;
};

const fromXmlUnits = (units) => {
  const negative = units < 0n;
  const unsigned = negative ? -units : units;
  const text = `${unsigned / XML_UNIT}.${(unsigned % XML_UNIT).toString().padStart(10, '0')}`;

  return Number(negative ? `-${text}` : text);
};

const sumUnits = (units) => units.reduce((total, next) => total + next, 0n);

const floorDivide = (dividend, divisor) => {
  const quotient = dividend / divisor;

  return dividend % divisor !== 0n && dividend < 0n !== divisor < 0n ? quotient - 1n : quotient;
};

const divideHalfEven = (dividend, divisor) => {
  const sign = divisor < 0n ? -1n : 1n;
  const quotient = floorDivide(dividend * sign, divisor * sign);
  const twiceRemainder = 2n * (dividend * sign - quotient * divisor * sign);
  const absDivisor = divisor * sign;
  if (twiceRemainder > absDivisor || (twiceRemainder === absDivisor && quotient % 2n !== 0n)) {
    return quotient + 1n;
  }

  return quotient;
};

const roundDecimalUnits = (units) => Number(floorDivide(units + CENT_UNITS / 2n, CENT_UNITS)) / 100;

// Document total rules (ibr-co-10, -13, -14, -15) round the exact xs:decimal value. Ties go toward +infinity.
// Matches SchematronRounding.roundDecimal on the API.
export const roundDecimal = (value) => roundDecimalUnits(toXmlUnits(value));

// IBR-147-AE reads line values as xs:double, so round(x * 100) div 100 runs in double.
// Matches SchematronRounding.roundDouble on the API.
export const roundDouble = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.round(number * 100) / 100;
};

const hasValue = (value) => value != null && value !== '';

const lineAllowanceChargeUnits = ({ amount, base_amount, multiplier_factor_numeric } = {}) => {
  if (hasValue(amount)) {
    return toXmlUnits(amount);
  }

  if (hasValue(base_amount) && hasValue(multiplier_factor_numeric)) {
    return divideHalfEven(toXmlUnits(base_amount) * toXmlUnits(multiplier_factor_numeric), XML_UNIT * 100n);
  }

  return 0n;
};

// The net stays exact unless IBR-147-AE, recomputed from the XML text in double, rounds to another cent.
// Then the net is that cent and the extension is the net minus line charges plus line allowances.
// Matches LineAmounts.resolve on the API.
const resolveLineAmounts = (quantity, priceAmount, baseQuantity, chargeUnits, allowanceUnits) => {
  const quantityUnits = toXmlUnits(quantity);
  const priceUnits = toXmlUnits(priceAmount);
  const baseQuantityUnits = toXmlUnits(baseQuantity);
  const adjustments = sumUnits(chargeUnits) - sumUnits(allowanceUnits);

  if (baseQuantityUnits === 0n) {
    const extension = toXmlUnits((quantity * priceAmount) / baseQuantity);

    return { extension, net: extension + adjustments };
  }

  const extension = divideHalfEven(quantityUnits * priceUnits, baseQuantityUnits);
  const net = extension + adjustments;
  const sumAsDouble = (units) => units.reduce((total, next) => total + fromXmlUnits(next), 0);
  const ibr147Cent = roundDouble(
    fromXmlUnits(quantityUnits) * (fromXmlUnits(priceUnits) / fromXmlUnits(baseQuantityUnits)) +
      sumAsDouble(chargeUnits) -
      sumAsDouble(allowanceUnits),
  );
  if (roundDouble(fromXmlUnits(net)) === ibr147Cent) {
    return { extension, net };
  }

  const centNet = BigInt(Math.round(ibr147Cent * 100)) * CENT_UNITS;

  return { extension: centNet - adjustments, net: centNet };
};

const exactAllowanceChargeAmount = (baseAmount, multiplierFactor, amount) => {
  if (hasValue(amount)) {
    return Number(amount);
  }

  if (hasValue(baseAmount) && hasValue(multiplierFactor)) {
    return (Number(baseAmount) * Number(multiplierFactor)) / 100;
  }

  return 0;
};

// Document allowance and charge amounts. PINT section 4.2 caps these at two decimals.
export const calculateAmount = (baseAmount, multiplierFactor, amount) =>
  halfUpToTwoDecimals(exactAllowanceChargeAmount(baseAmount, multiplierFactor, amount));

const sumAllowanceChargeAmounts = (entries = []) =>
  (entries || []).reduce(
    (total, { amount, base_amount, multiplier_factor_numeric }) =>
      total + calculateAmount(base_amount, multiplier_factor_numeric, amount),
    0,
  );

// Document-level sums. Each amount and the sum are two decimals.
export const calculateTotalAllowances = (allowances = []) => halfUpToTwoDecimals(sumAllowanceChargeAmounts(allowances));

export const calculateTotalCharges = (charges = []) => halfUpToTwoDecimals(sumAllowanceChargeAmounts(charges));

// Line allowance and charge amounts keep the XML scale.
export const calculateLineAllowanceTotal = (allowances = []) =>
  fromXmlUnits(sumUnits((allowances || []).map(lineAllowanceChargeUnits)));

export const calculateLineChargeTotal = (charges = []) =>
  fromXmlUnits(sumUnits((charges || []).map(lineAllowanceChargeUnits)));

export const resolvePriceBaseAmount = (price = {}) => {
  if (price.base_amount != null) {
    return Number(price.base_amount);
  }

  if (price.price_amount != null) {
    return Number(price.price_amount);
  }

  if (price.allowance?.base_amount != null) {
    return Number(price.allowance.base_amount);
  }

  return 0;
};

export const normalizeLineItemPrice = (price) => {
  if (!price) {
    return price;
  }

  const baseAmount = resolvePriceBaseAmount(price);

  return {
    ...price,
    base_amount: baseAmount,
    base_quantity: price.base_quantity || 1,
    allowance: price.allowance
      ? {
          ...price.allowance,
          base_amount: price.allowance.base_amount ?? baseAmount,
        }
      : price.allowance,
  };
};

export const buildSubmitLineItemPrice = (price = {}) => {
  const baseAmount = resolvePriceBaseAmount(price);

  return {
    base_amount: baseAmount,
    base_quantity: price.base_quantity || 1,
    allowance: {
      amount: price.allowance?.amount || 0,
      base_amount: baseAmount,
      reason_code: price.allowance?.reason_code || null,
      reason: price.allowance?.reason || null,
      tax_category: price.allowance?.tax_category || null,
    },
  };
};

const getTaxCategoryCode = (taxCategory) => taxCategory?.id ?? 'S';

const getTaxRate = (taxCategory) => (taxCategory?.percent != null ? Number(taxCategory.percent) : 0);

const getTaxGroupKey = (categoryCode, taxRate) => `${categoryCode}-${taxRate}`;

const calculateBreakdownTaxUnits = (categoryCode, taxableUnits, rate) => {
  if (calculatesZeroTaxAmount(categoryCode)) {
    return 0n;
  }

  return divideHalfEven(taxableUnits * toXmlUnits(rate), XML_UNIT * 100n);
};

const addToBreakdown = (breakdownMap, categoryCode, taxRate, units) => {
  const key = getTaxGroupKey(categoryCode, taxRate);
  if (!breakdownMap[key]) {
    breakdownMap[key] = { category: categoryCode, rate: taxRate, taxableUnits: 0n };
  }
  breakdownMap[key].taxableUnits += units;
};

const toBreakdownAmounts = (breakdownMap) =>
  Object.fromEntries(
    Object.entries(breakdownMap).map(([key, { category, rate, taxableUnits }]) => [
      key,
      {
        category,
        rate,
        taxableAmount: fromXmlUnits(taxableUnits),
        taxAmount: fromXmlUnits(calculateBreakdownTaxUnits(category, taxableUnits, rate)),
      },
    ]),
  );

const collectTaxableAmounts = ({ items = [], docCharges = [], docAllowances = [] }) => {
  const chargesBreakdown = {};
  const allowancesBreakdown = {};
  const lineItemsBreakdown = {};
  const taxGroupMap = {};
  let nonTaxableUnits = 0n;

  const addEntry = (breakdownMap, taxCategory, units) => {
    if (!taxCategory) {
      nonTaxableUnits += units;
      return;
    }

    const categoryCode = getTaxCategoryCode(taxCategory);
    const taxRate = getTaxRate(taxCategory);
    addToBreakdown(breakdownMap, categoryCode, taxRate, units);

    if (taxRate <= 0) {
      nonTaxableUnits += units;
      return;
    }

    addToBreakdown(taxGroupMap, categoryCode, taxRate, units);
  };

  items.forEach((item) => {
    addEntry(lineItemsBreakdown, item.classified_tax_category, toXmlUnits(lineItemCalculation(item).net_amount));
  });

  docCharges.forEach((charge) => {
    const chargeAmount = calculateAmount(charge.base_amount, charge.multiplier_factor_numeric, charge.amount);
    addEntry(chargesBreakdown, charge.tax_category, toXmlUnits(chargeAmount));
  });

  docAllowances.forEach((allowance) => {
    const allowanceAmount = calculateAmount(
      allowance.base_amount,
      allowance.multiplier_factor_numeric,
      allowance.amount,
    );
    addEntry(allowancesBreakdown, allowance.tax_category, -toXmlUnits(allowanceAmount));
  });

  const groups = Object.values(taxGroupMap);
  const totalTaxUnits = sumUnits(
    groups.map(({ category, taxableUnits, rate }) => calculateBreakdownTaxUnits(category, taxableUnits, rate)),
  );
  const taxableUnits = sumUnits(groups.filter(({ rate }) => rate > 0).map(({ taxableUnits: units }) => units));
  const allowanceAmounts = toBreakdownAmounts(allowancesBreakdown);
  Object.values(allowanceAmounts).forEach((breakdown) => {
    breakdown.taxableAmount = -breakdown.taxableAmount;
    breakdown.taxAmount = -breakdown.taxAmount;
  });

  return {
    totalTaxUnits,
    result: {
      totalTaxableAmount: fromXmlUnits(taxableUnits),
      totalNonTaxableAmount: fromXmlUnits(nonTaxableUnits),
      // Exact category tax sum; callers round it for IBT-110 / document totals.
      totalTaxAmount: fromXmlUnits(totalTaxUnits),
      taxBreakdown: {
        allowances: allowanceAmounts,
        charges: toBreakdownAmounts(chargesBreakdown),
        lineItems: toBreakdownAmounts(lineItemsBreakdown),
      },
    },
  };
};

// Function to calculate taxable and non-taxable amounts with tax breakdown
export const calculateTaxableAmounts = (args) => collectTaxableAmounts(args).result;

const resolvePriceDiscount = (price = {}) => {
  if (hasValue(price.allowance?.amount)) {
    return Number(price.allowance.amount);
  }

  if (hasValue(price.allowance_amount)) {
    return Number(price.allowance_amount);
  }

  return 0;
};

const resolveBaseQuantity = (price = {}) => {
  const baseQuantity = Number(price.base_quantity);
  return baseQuantity ? baseQuantity : 1;
};

export const priceExtension = (item = {}) => {
  const baseAmount = resolvePriceBaseAmount(item.price);
  const priceAmount = baseAmount - resolvePriceDiscount(item.price);
  const quantity = Number(item.quantity) || 0;

  return (priceAmount / resolveBaseQuantity(item.price)) * quantity;
};

export const lineItemCalculation = (item) => {
  const priceAmount = resolvePriceBaseAmount(item.price) - resolvePriceDiscount(item.price);
  const chargeUnits = (item?.charges || []).map(lineAllowanceChargeUnits);
  const allowanceUnits = (item?.allowances || []).map(lineAllowanceChargeUnits);
  const { extension, net } = resolveLineAmounts(
    Number(item.quantity) || 0,
    priceAmount,
    resolveBaseQuantity(item.price),
    chargeUnits,
    allowanceUnits,
  );
  const calculatedBaseAmount = fromXmlUnits(extension);
  const netAmount = fromXmlUnits(net);
  const allowanceAmount = fromXmlUnits(sumUnits(allowanceUnits));
  const chargeAmounts = fromXmlUnits(sumUnits(chargeUnits));

  const categoryCode = getTaxCategoryCode(item?.classified_tax_category);
  const taxRate = getTaxRate(item?.classified_tax_category);
  const effectiveRate = effectiveTaxRateForCalculation(categoryCode, taxRate);
  const taxAmount = (netAmount * effectiveRate) / 100;
  const totalAmount = netAmount + taxAmount;

  return {
    ...item,
    allowance_amount: Number(allowanceAmount || 0),
    price: item.price,
    unit_price: Number(item.unit_price || 0),
    quantity: Number(item.quantity || 0),
    base_amount: calculatedBaseAmount,
    net_amount: netAmount,
    charge_amount: chargeAmounts,
    tax_amount: taxAmount,
    total_amount: totalAmount,
    tax_rate: Number(item?.tax_category?.percent || 0),
  };
};

const pricingEngineForDocuments = ({
  items = [],
  docCharges = [],
  docAllowances = [],
  prePayment = {},
  conversionRatePerAED,
  payableRoundingAmount,
}) => {
  if (!Array.isArray(items)) {
    throw new Error("Expected 'items' to be an array.");
  }

  const calculatedLines = items.map(lineItemCalculation);
  const itemsSubtotal = calculatedLines.reduce((acc, item) => acc + item.base_amount, 0);
  const totalItemAllowances = calculatedLines.reduce((acc, item) => acc + item.allowance_amount, 0);

  const totalItemCharges = calculatedLines.reduce((acc, item) => acc + item.charge_amount, 0);

  const totalDocumentAllowances = calculateTotalAllowances(docAllowances);
  const totalDocumentCharges = calculateTotalCharges(docCharges);

  const headerCharges = calculateTotalCharges(docCharges);
  const headerAllowance = calculateTotalAllowances(docAllowances);

  const {
    totalTaxUnits,
    result: { totalTaxableAmount, totalNonTaxableAmount, taxBreakdown },
  } = collectTaxableAmounts({ items, docCharges, docAllowances });

  // Match API: decimal round of line extension, tax exclusive, tax sum, and tax exclusive + exact category tax.
  const subtotal = roundDecimalUnits(sumUnits(calculatedLines.map((line) => toXmlUnits(line.net_amount))));
  const totalExcludingTaxRounded = roundDecimalUnits(
    toXmlUnits(subtotal) - toXmlUnits(headerAllowance) + toXmlUnits(headerCharges),
  );
  const totalTaxAmount = roundDecimalUnits(totalTaxUnits);
  const totalIncludingTax = roundDecimalUnits(toXmlUnits(totalExcludingTaxRounded) + totalTaxUnits);
  const totalPayableAmount = totalIncludingTax - (prePayment?.amount || 0) + (payableRoundingAmount || 0);

  return {
    // Item subtotal stays exact (PDF fmtExact). Line extension, tax, tax exclusive and
    // tax inclusive follow the API decimal round. Allowances, charges, payable, AED stay half-up.
    itemsSubtotal,
    subtotal,
    charges: docCharges,
    allowances: docAllowances,
    headerCharges: halfUpToTwoDecimals(headerCharges),
    headerAllowance: halfUpToTwoDecimals(headerAllowance),
    totalTaxAmount,
    taxInAED: conversionRatePerAED ? halfUpToTwoDecimals(totalTaxAmount * conversionRatePerAED) : null,
    totalItemAllowances,
    totalItemCharges,
    totalDocumentAllowances: halfUpToTwoDecimals(totalDocumentAllowances),
    totalDocumentCharges: halfUpToTwoDecimals(totalDocumentCharges),
    totalExcludingTax: totalExcludingTaxRounded,
    totalIncludingTax,
    totalPayableAmount: halfUpToTwoDecimals(totalPayableAmount),
    totalPaid: 0, //Todo :- implement total paid later
    totalTaxableAmount: totalTaxableAmount,
    totalNonTaxableAmount: totalNonTaxableAmount,
    taxBreakdown: taxBreakdown,
    payableRoundingAmount: payableRoundingAmount ? halfUpToTwoDecimals(payableRoundingAmount) : null,
  };
};

export default pricingEngineForDocuments;

export const calculateRegisterPricingEngine = ({ items, conversionRatePerAED }) => {
  const { totalExcludingTax, totalTaxAmount, taxInAED, totalPayableAmount, totalPaybleAmountInAED } = items.reduce(
    (acc, item) => {
      const { net_amount, classified_tax_category } = item;
      const categoryCode = classified_tax_category?.id ?? 'S';
      const taxRate = classified_tax_category?.percent || 0;
      const effectiveRate = effectiveTaxRateForCalculation(categoryCode, taxRate);
      const taxAmount = (net_amount * effectiveRate) / 100;
      const lineTotal = net_amount + taxAmount;
      return {
        totalExcludingTax: acc.totalExcludingTax + net_amount,
        totalTaxAmount: acc.totalTaxAmount + taxAmount,
        taxInAED: acc.taxInAED + taxAmount * conversionRatePerAED,
        totalPayableAmount: acc.totalPayableAmount + lineTotal,
        totalPaybleAmountInAED: acc.totalPaybleAmountInAED + lineTotal * conversionRatePerAED,
      };
    },
    { totalExcludingTax: 0, totalTaxAmount: 0, taxInAED: 0, totalPayableAmount: 0, totalPaybleAmountInAED: 0 },
  );

  return {
    totalExcludingTax: roundDecimal(totalExcludingTax),
    totalTaxAmount: roundDecimal(totalTaxAmount),
    taxInAED: halfUpToTwoDecimals(taxInAED),
    totalPayableAmount: halfUpToTwoDecimals(totalPayableAmount),
    totalPaybleAmountInAED: halfUpToTwoDecimals(totalPaybleAmountInAED),
  };
};
